import { ArrowLeft, ChevronDown, ChevronRight, SearchX } from "lucide-react";
import { Button, Skeleton } from "@capacit-com/lara-law-shared-ui";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { StaleSearchIndexNotice } from "@/components/indexing-status/StaleSearchIndexNotice";
import { useStaleSearchIndex } from "@/hooks/useStaleSearchIndex";
import {
  CONTENT_SEARCH_DOCUMENTS_CAP,
  useWorkspaceContentSearch,
} from "@/hooks/useWorkspaceContentSearch";
import { ContentSearchWorkspaceCard } from "./ContentSearchWorkspaceCard";
import { documentCountLabel, workspaceCountLabel } from "./contentSearchLabels";

/** The overview shows a handful of workspaces with their best hits; zooming shows one workspace in full. */
export const OVERVIEW_WORKSPACE_PAGE_SIZE = 5;
export const OVERVIEW_DOCUMENTS_PER_WORKSPACE = 3;

interface WorkspaceContentSearchResultsProps {
  /** The search as typed, already debounced. */
  searchTerm: string;
  /** The workspace zoomed into, or null for the overview across all workspaces. */
  zoomedWorkspaceId: string | null;
  onZoomWorkspace: (workspaceId: string | null) => void;
  className?: string;
}

/**
 * The staircase of documents whose title or text matches the Workspaces page search
 * (LAR-235): workspace → folder → document, grouped per workspace. The overview caps
 * what it shows and lets the reader zoom into one workspace; the zoomed view lists every
 * hit of that workspace with a way back.
 */
export const WorkspaceContentSearchResults = ({
  searchTerm,
  zoomedWorkspaceId,
  onZoomWorkspace,
  className,
}: WorkspaceContentSearchResultsProps) => {
  const zoomed = zoomedWorkspaceId !== null;
  const term = searchTerm.trim();

  const {
    workspaces,
    totalDocuments,
    totalWorkspaces,
    isSearchable,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useWorkspaceContentSearch({
    searchTerm: term,
    workspaceIds: zoomed ? [zoomedWorkspaceId] : undefined,
    workspacePageSize: zoomed ? 1 : OVERVIEW_WORKSPACE_PAGE_SIZE,
    documentsPerWorkspace: zoomed
      ? CONTENT_SEARCH_DOCUMENTS_CAP
      : OVERVIEW_DOCUMENTS_PER_WORKSPACE,
  });

  // Outdated search indexes among the hits: the same marker, notice and rerun flow as the
  // files tab and global search, so the amber icon reads the same wherever it shows up.
  const visibleDocuments = useMemo(
    () => workspaces.flatMap((workspace) => workspace.documents),
    [workspaces]
  );
  const staleIndex = useStaleSearchIndex(visibleDocuments);

  if (!isSearchable) {
    return null;
  }

  const zoomedWorkspace = zoomed
    ? (workspaces.find((workspace) => workspace.id === zoomedWorkspaceId) ??
      workspaces[0])
    : undefined;
  const remainingWorkspaces = Math.max(0, totalWorkspaces - workspaces.length);

  return (
    <section
      aria-labelledby="workspace-content-search-title"
      className={cn("flex flex-col gap-2", className)}
    >
      {zoomed ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            onClick={() => onZoomWorkspace(null)}
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Alle workspaces
          </button>
          <ChevronRight
            className="size-3.5 text-muted-foreground"
            aria-hidden="true"
          />
          <h2
            id="workspace-content-search-title"
            className="min-w-0 truncate text-sm font-semibold"
          >
            {zoomedWorkspace?.name ?? "Workspace"}
          </h2>
          {zoomedWorkspace && (
            <span className="text-xs text-muted-foreground">
              {documentCountLabel(zoomedWorkspace.matchingDocumentCount)} med
              match på “{term}”
            </span>
          )}
        </div>
      ) : (
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h2
            id="workspace-content-search-title"
            className="text-sm font-semibold"
          >
            Dokumenter med match på “{term}”
          </h2>
          {!isPending && !isError && (
            <span className="text-xs text-muted-foreground">
              {documentCountLabel(totalDocuments)} i{" "}
              {workspaceCountLabel(totalWorkspaces)}
              {totalWorkspaces > 0 &&
                ` · viser ${OVERVIEW_DOCUMENTS_PER_WORKSPACE} pr. workspace`}
            </span>
          )}
          <span className="grow" />
          <span className="hidden text-xs text-muted-foreground xl:inline">
            Klik på et dokument for at åbne det med søgeordet markeret
          </span>
        </div>
      )}

      {!isPending && !isError && (
        <StaleSearchIndexNotice
          scope="visible"
          staleCount={staleIndex.staleCount}
          reindexableCount={staleIndex.reindexableCount}
          runningCount={staleIndex.runningCount}
          batchSize={staleIndex.batchSize}
          onReindexAll={staleIndex.reindexAll}
        />
      )}

      {isPending ? (
        <div
          className="flex flex-col gap-2"
          role="status"
          aria-label="Søger i dokumenter"
        >
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="ml-6 h-8 w-[calc(100%-1.5rem)]" />
          <Skeleton className="ml-6 h-8 w-[calc(100%-1.5rem)]" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          Søgningen i dokumenter kunne ikke gennemføres. Prøv igen om et
          øjeblik.
        </p>
      ) : workspaces.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-border px-6 py-7 text-center">
          <SearchX
            className="size-6 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="text-sm font-semibold">
            Ingen dokumenter matcher “{term}”
          </p>
          <p className="max-w-md text-xs text-muted-foreground">
            Søgningen dækker titler og indhold i{" "}
            {zoomed ? "dette workspace" : "alle dine workspaces"}.
          </p>
        </div>
      ) : (
        <>
          {workspaces.map((workspace) => (
            <ContentSearchWorkspaceCard
              key={workspace.id}
              workspace={workspace}
              searchTerm={term}
              zoomed={zoomed}
              onZoom={onZoomWorkspace}
              indexingDocumentIds={staleIndex.indexingDocumentIds}
              onReindex={staleIndex.reindex}
            />
          ))}
          {hasNextPage && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={isFetchingNextPage}
              onClick={() => {
                void fetchNextPage();
              }}
            >
              <ChevronDown className="size-4" aria-hidden="true" />
              Vis flere workspaces
              {remainingWorkspaces > 0 && (
                <span className="font-normal text-muted-foreground">
                  ({remainingWorkspaces} tilbage)
                </span>
              )}
            </Button>
          )}
        </>
      )}
    </section>
  );
};

export default WorkspaceContentSearchResults;
